
  import EmployeeService from "@/service/EmployeeService";
  import EmployeeWageForm from "../forms/EmployeeWageForm.vue";
  import {AxiosError} from "axios";
  import Wage from "@/model/Wage";
  import ModalDialog from "@/components/ModalDialog.vue";
  import {computed, defineComponent, reactive, Ref, SetupContext, toRefs, watch} from "vue";
  import {helpers, required, requiredIf} from "@vuelidate/validators";
  import useVuelidate from "@vuelidate/core";
  import moment from "moment";
  import store from "@/store/store";
  import {isAuthorized} from "@/mixin/mixins";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";

  export default defineComponent({
    name: "WageDialog",
    components: {
      EmployeeWageForm,
      ModalDialog
    },
    props: {
      editable: {
        type: Boolean,
        default: false
      },
      employeeUuid: {
        type: String,
        default: null
      },
      wage: {
        type: Wage,
        default: () => new Wage(),
      },
      value: {
        type: Boolean,
        default: false
      }
    },
    setup(props, context: SetupContext) {

      const initialState = {
        editItem: new Wage() as Wage,
        serverErrors: new Map<string, string>()
      };

      const state = reactive({...initialState});

      const rules = computed(() => ({
        editItem: {
          validFrom: {
            required: helpers.withMessage("Gültig ab ist erforderlich.", required),
          },
          wage: {
            required: helpers.withMessage("Gehalt ist erforderlich.", required),
          },
          wageType: {
            required: helpers.withMessage("Lohnart ist erforderlich.", required),
          },
          targetHours: {
            required: helpers.withMessage("Sollstunden ist erforderlich.", requiredIf(function () {
              return isSalary.value;
            })),
          }
        }
      }));

      // @ts-ignore
      const v$ = useVuelidate(rules, state);

      const errors = computed(() => {
        const errors = new Map<string, string | Ref<string>>();
        if (state.serverErrors.size > 0) {
          return state.serverErrors;
        } else {
          const model = v$.value.editItem;
          if (model.$dirty) {
            if(model.validFrom.$error) {
              errors.set("validFrom", model.validFrom.$errors[0].$message);
            }
            if(model.wage.$error) {
              errors.set("wage", model.wage.$errors[0].$message);
            }
            if(model.wageType.$error) {
              errors.set("wageType", model.wageType.$errors[0].$message);
            }
            if(model.targetHours.$error) {
              errors.set("targetHours", model.targetHours.$errors[0].$message);
            }
          }
        }

        return errors;
      });

      const deletable = computed(() => {
        // @ts-ignore
        return props.editable && isAuthorized("Superadmin") && !!props.wage && !!props.wage.uuid;
      });

      const earliestValidFrom = computed(() => {
        return store.state.general.earliestValidFrom;
      });

      const isEditable = computed(() => {
        return props.editable && props.wage && moment(props.wage.validFrom).isSameOrAfter(moment(earliestValidFrom.value));
      });

      const isSalary = computed(() => {
        return state.editItem && state.editItem.wageType && state.editItem.wageType === "SAL";
      });

      const smartphone = computed(() => {
        return store.state.general.earliestValidFrom;
      });

      const close = () => {
        context.emit("input", false);
        context.emit("close");
      };

      // The v3 API answers a rejected wage with a problem detail instead of errors per field. A 409 is already shown
      // by the global error handler, a 422 is not.
      const showProblem = (error: AxiosError) => {
        if (error && error.response && error.response.status === 422) {
          // @ts-ignore
          eventBus.$emit(MessageTypes.ERROR, error.response.data.detail || error.response.data.title);
        }
      };

      const deleteWage = () => {
        EmployeeService.deleteWageV3(props.employeeUuid, props.wage.uuid as string)
          .then(response => {
            context.emit("delete", props.wage);
            close();
          })
          .catch(showProblem);
      };

      const save = () => {
        state.serverErrors = new Map<string, string>();
        v$.value.$reset();
        v$.value.$touch();

        if (!v$.value.$invalid) {
          const wage = state.editItem;
          const targetHours = wage.targetHours === null || wage.targetHours === undefined || `${wage.targetHours}` === ""
            ? null
            : Number(wage.targetHours);
          let promise = null;
          let eventType = "";
          if (props.wage.uuid) {
            promise = EmployeeService.updateWageV3(props.employeeUuid, props.wage.uuid, {
              amount: wage.wage,
              wageType: wage.wageType,
              targetHours: targetHours
            });
            eventType = "update";
          } else {
            promise = EmployeeService.createWageV3(props.employeeUuid, {
              amount: wage.wage,
              wageType: wage.wageType,
              targetHours: targetHours,
              validFrom: moment(wage.validFrom).format("YYYY-MM")
            });
            eventType = "create";
          }

          promise
            .then(wagePeriod => {
              context.emit(eventType, wagePeriod);
              close();
            })
            .catch(showProblem);
        }
      };

      watch(() => props.wage, (newValue, oldValue) => {
        v$.value.$reset();
        state.editItem = Object.assign(new Wage(), newValue);
      });

      return {
        ...toRefs(state),
        v$,
        errors,
        deletable,
        isEditable,
        smartphone,
        close,
        deleteWage,
        save
      };
    }
  });
